import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Building2 } from 'lucide-react';

const CreateTaskDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  llcs,
  users,
  priorityOptions,
  formLoading,
  onSubmit,
  onToggleLlcSelection,
  onToggleAllLlcs
}) => {
  // Sort LLCs alphabetically
  const sortedLlcs = useMemo(() => {
    return [...llcs].sort((a, b) => a.name.localeCompare(b.name));
  }, [llcs]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="font-serif">Create New Task</DialogTitle>
          <DialogDescription>Add a new task to one or more LLCs</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>LLCs ({formData.llc_ids?.length || 0} selected)</Label>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onToggleAllLlcs}
                className="h-7 text-xs"
              >
                {formData.llc_ids?.length === llcs.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            <div className="border rounded-md max-h-[180px] overflow-y-auto p-2 space-y-1">
              {sortedLlcs.map((llc) => (
                <div 
                  key={llc.id} 
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 transition-colors ${formData.llc_ids?.includes(llc.id) ? 'bg-amber-50' : ''}`}
                  onClick={() => onToggleLlcSelection(llc.id)}
                >
                  <Checkbox 
                    checked={formData.llc_ids?.includes(llc.id)}
                    onCheckedChange={() => onToggleLlcSelection(llc.id)}
                    className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                  />
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">{llc.name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Assign To</Label>
            <Select value={formData.assigned_to_id} onValueChange={(v) => setFormData({...formData, assigned_to_id: v})}>
              <SelectTrigger data-testid="task-assignee-select">
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
              <SelectTrigger data-testid="task-priority-select">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                {priorityOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <opt.icon className="w-4 h-4" />
                      {opt.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Task Description</Label>
            <Input
              value={formData.task_description}
              onChange={(e) => setFormData({...formData, task_description: e.target.value})}
              placeholder="Enter task description"
              data-testid="task-description-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Date Needed</Label>
            <Input
              type="date"
              value={formData.date_needed}
              onChange={(e) => setFormData({...formData, date_needed: e.target.value})}
              data-testid="task-date-needed-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={formLoading || (formData.llc_ids?.length || 0) === 0} data-testid="save-task-btn">
            {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {(formData.llc_ids?.length || 0) > 1 ? `Create ${formData.llc_ids.length} Tasks` : 'Create Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTaskDialog;
